import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Activity, Database, HardDrive, Clock, Users, FileText, 
  MessageSquare, Trash2, RefreshCw, Server, Zap, AlertTriangle,
  CheckCircle, ArrowLeft, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSystemHealthPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const getAdminToken = () => sessionStorage.getItem('admin_token');
  const getAdminHeader = () => ({ Authorization: `Bearer ${getAdminToken()}` });

  const fetchMetrics = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/system-health`, {
        headers: getAdminHeader()
      });
      setMetrics(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchMetrics();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchMetrics, 30000);
    return () => clearInterval(interval);
  }, [fetchMetrics]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchMetrics();
  };

  const handleCleanup = async () => {
    if (!confirm('This will delete files older than 90 days. Continue?')) return;
    
    setCleaningUp(true);
    try {
      const response = await axios.post(`${API}/admin/cleanup-files?days_old=90`, {}, {
        headers: getAdminHeader()
      });
      toast.success(`Cleanup complete: ${response.data.result.cleaned} files removed`);
      fetchMetrics();
    } catch (error) {
      toast.error('Cleanup failed');
    } finally {
      setCleaningUp(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-teal-500 animate-spin" />
      </div>
    );
  }

  const { database, collections, sms_queue, recent_activity_24h, file_storage, uptime, request_stats } = metrics || {};

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              className="text-slate-400 hover:text-white"
              onClick={() => navigate('/admin/dashboard')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-white">System Health</h1>
              <p className="text-slate-400">Real-time monitoring and metrics</p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="border-slate-700 text-slate-300"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              variant="outline" 
              className="border-red-700 text-red-400 hover:bg-red-900"
              onClick={handleCleanup}
              disabled={cleaningUp}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {cleaningUp ? 'Cleaning...' : 'Cleanup Old Files'}
            </Button>
          </div>
        </div>

        {/* Status Banner */}
        <Card className="bg-slate-800 border-slate-700 mb-6">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                <span className="text-white font-medium">All Systems Operational</span>
              </div>
              <div className="flex items-center gap-2 text-slate-400">
                <Clock className="w-4 h-4" />
                <span>Uptime: {uptime?.uptime_human || 'N/A'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Metrics Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Database Size */}
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Database Size</p>
                  <p className="text-2xl font-bold text-white">{database?.size_mb || 0} MB</p>
                </div>
                <Database className="w-8 h-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>

          {/* Total Intakes */}
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Intakes</p>
                  <p className="text-2xl font-bold text-white">{collections?.intakes || 0}</p>
                </div>
                <FileText className="w-8 h-8 text-teal-400" />
              </div>
            </CardContent>
          </Card>

          {/* Total Users */}
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Registered Users</p>
                  <p className="text-2xl font-bold text-white">{collections?.users || 0}</p>
                </div>
                <Users className="w-8 h-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>

          {/* File Storage */}
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">File Storage</p>
                  <p className="text-2xl font-bold text-white">{file_storage?.total_size_mb || 0} MB</p>
                  <p className="text-xs text-slate-500">{file_storage?.total_files || 0} files</p>
                </div>
                <HardDrive className="w-8 h-8 text-orange-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Second Row */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {/* SMS Queue */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-teal-400" />
                SMS Queue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-400">Pending</span>
                  <Badge variant="outline" className="border-yellow-500 text-yellow-400">
                    {sms_queue?.pending || 0}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Sent Total</span>
                  <span className="text-green-400">{sms_queue?.sent_total || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Failed Total</span>
                  <span className="text-red-400">{sms_queue?.failed_total || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-green-400" />
                Last 24 Hours
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-400">New Intakes</span>
                  <span className="text-white font-medium">{recent_activity_24h?.new_intakes || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">New Users</span>
                  <span className="text-white font-medium">{recent_activity_24h?.new_users || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Request Stats */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-400" />
                API Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-400">Total Requests</span>
                  <span className="text-white font-medium">{request_stats?.total_requests || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Error Rate</span>
                  <span className={request_stats?.error_rate > 5 ? 'text-red-400' : 'text-green-400'}>
                    {request_stats?.error_rate || 0}%
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top Endpoints */}
        {request_stats?.top_endpoints && Object.keys(request_stats.top_endpoints).length > 0 && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Top API Endpoints</CardTitle>
              <CardDescription className="text-slate-400">Most frequently accessed endpoints</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {Object.entries(request_stats.top_endpoints).slice(0, 10).map(([endpoint, count]) => (
                  <div key={endpoint} className="flex items-center justify-between py-2 border-b border-slate-700 last:border-0">
                    <code className="text-teal-400 text-sm">{endpoint}</code>
                    <Badge variant="secondary" className="bg-slate-700 text-slate-300">
                      {count} requests
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminSystemHealthPage;
